import { useRef, useEffect, useState, useCallback } from 'react';

const W = 360;
const H = 480;
const BALL_R = 7;
const GRAVITY = 0.16;
const MAX_SPEED = 11;
const WALL_DAMP = 0.82;
const FLIP_LEN = 62;
const FLIP_R = 9;
const REST_ANGLE = 0.42;   // flipper resting, tilted down
const UP_ANGLE = -0.55;    // flipper flipped up
const FLIP_SPEED = 0.3;    // rad per frame
const BALLS_PER_GAME = 3;  // "locate tickets"

// Pop bumpers = buried utilities, APWA locate colors
const BUMPERS = [
  { x: 105, y: 150, r: 22, color: '#e0a800', label: 'GAS' },
  { x: 255, y: 150, r: 22, color: '#d32f2f', label: 'ELEC' },
  { x: 180, y: 235, r: 22, color: '#2563eb', label: 'WTR' },
];

// Light 8-1-1 for the bonus
const TARGETS = [
  { x: 100, y: 55, label: '8' },
  { x: 180, y: 55, label: '1' },
  { x: 260, y: 55, label: '1' },
];
const TARGET_R = 11;

// Slanted walls funneling the ball toward the flippers
const GUIDES = [
  { ax: 0, ay: 318, bx: 101, by: 441 },
  { ax: W, ay: 318, bx: 259, by: 441 },
];
const GUIDE_R = 4;

const FLIPPERS = [
  { px: 106, py: 446, sign: 1, key: 'left' },
  { px: 254, py: 446, sign: -1, key: 'right' },
];

function closestOnSeg(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy || 1;
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return { x: ax + dx * t, y: ay + dy * t };
}

export default function PinballGame({ onScore }) {
  const canvasRef = useRef();
  const stateRef = useRef();
  const rafRef = useRef();
  const [score, setScore] = useState(0);
  const [tickets, setTickets] = useState(BALLS_PER_GAME);
  const [status, setStatus] = useState('idle'); // idle | playing | over

  const loop = useCallback(() => {
    const s = stateRef.current;
    const ctx = canvasRef.current.getContext('2d');

    // flippers swing toward pressed/rest angle
    FLIPPERS.forEach((f) => {
      const st = s.flippers[f.key];
      const target = s.keys[f.key] ? UP_ANGLE : REST_ANGLE;
      const prev = st.a;
      if (st.a < target) st.a = Math.min(target, st.a + FLIP_SPEED);
      else if (st.a > target) st.a = Math.max(target, st.a - FLIP_SPEED);
      st.da = st.a - prev;
    });

    // respawn countdown between balls
    if (!s.ball && !s.over) {
      s.respawn -= 1;
      if (s.respawn <= 0) {
        s.ball = { x: 60 + Math.random() * 240, y: 28, vx: (Math.random() - 0.5) * 2, vy: 0 };
      }
    }

    const b = s.ball;
    if (b) {
      b.vy += GRAVITY;
      b.x += b.vx;
      b.y += b.vy;

      // side + top walls
      if (b.x < BALL_R) { b.x = BALL_R; b.vx = Math.abs(b.vx) * WALL_DAMP; }
      if (b.x > W - BALL_R) { b.x = W - BALL_R; b.vx = -Math.abs(b.vx) * WALL_DAMP; }
      if (b.y < BALL_R) { b.y = BALL_R; b.vy = Math.abs(b.vy) * WALL_DAMP; }

      // slanted guides
      GUIDES.forEach((g) => {
        const c = closestOnSeg(b.x, b.y, g.ax, g.ay, g.bx, g.by);
        const dx = b.x - c.x, dy = b.y - c.y;
        const dist = Math.hypot(dx, dy);
        if (dist > 0 && dist < BALL_R + GUIDE_R) {
          const nx = dx / dist, ny = dy / dist;
          b.x = c.x + nx * (BALL_R + GUIDE_R + 0.1);
          b.y = c.y + ny * (BALL_R + GUIDE_R + 0.1);
          const vn = b.vx * nx + b.vy * ny;
          if (vn < 0) { b.vx -= 1.7 * vn * nx; b.vy -= 1.7 * vn * ny; }
        }
      });

      // flippers (capsules with surface velocity while swinging)
      FLIPPERS.forEach((f) => {
        const st = s.flippers[f.key];
        const tipx = f.px + f.sign * Math.cos(st.a) * FLIP_LEN;
        const tipy = f.py + Math.sin(st.a) * FLIP_LEN;
        const c = closestOnSeg(b.x, b.y, f.px, f.py, tipx, tipy);
        const dx = b.x - c.x, dy = b.y - c.y;
        const dist = Math.hypot(dx, dy);
        if (dist > 0 && dist < BALL_R + FLIP_R) {
          const nx = dx / dist, ny = dy / dist;
          b.x = c.x + nx * (BALL_R + FLIP_R + 0.1);
          b.y = c.y + ny * (BALL_R + FLIP_R + 0.1);
          const rx = c.x - f.px, ry = c.y - f.py;
          const omega = f.sign * st.da;
          const surfVx = omega * -ry, surfVy = omega * rx;
          const vn = (b.vx - surfVx) * nx + (b.vy - surfVy) * ny;
          if (vn < 0) {
            b.vx -= 1.25 * vn * nx;
            b.vy -= 1.25 * vn * ny;
          }
        }
      });

      // utility bumpers
      s.bumpers.forEach((bp, i) => {
        const dx = b.x - bp.x, dy = b.y - bp.y;
        const dist = Math.hypot(dx, dy);
        if (dist > 0 && dist < BALL_R + bp.r) {
          const nx = dx / dist, ny = dy / dist;
          b.x = bp.x + nx * (BALL_R + bp.r + 0.1);
          b.y = bp.y + ny * (BALL_R + bp.r + 0.1);
          const speed = Math.max(Math.hypot(b.vx, b.vy) * 0.7, 6.5);
          b.vx = nx * speed;
          b.vy = ny * speed;
          s.bumperFlash[i] = 8;
          s.score += 50;
        }
      });

      // 8-1-1 targets
      TARGETS.forEach((t, i) => {
        const dx = b.x - t.x, dy = b.y - t.y;
        const dist = Math.hypot(dx, dy);
        if (dist > 0 && dist < BALL_R + TARGET_R) {
          const nx = dx / dist, ny = dy / dist;
          b.x = t.x + nx * (BALL_R + TARGET_R + 0.1);
          b.y = t.y + ny * (BALL_R + TARGET_R + 0.1);
          const vn = b.vx * nx + b.vy * ny;
          if (vn < 0) { b.vx -= 1.8 * vn * nx; b.vy -= 1.8 * vn * ny; }
          if (!s.lit[i]) {
            s.lit[i] = true;
            s.score += 100;
            if (s.lit.every(Boolean)) {
              s.score += 811;
              s.bonusFlash = 90;
              s.lit = [false, false, false];
            }
          }
        }
      });

      // speed cap (also keeps collisions from tunneling)
      const sp = Math.hypot(b.vx, b.vy);
      if (sp > MAX_SPEED) { b.vx = (b.vx / sp) * MAX_SPEED; b.vy = (b.vy / sp) * MAX_SPEED; }

      // drain
      if (b.y > H + 24) {
        s.ball = null;
        if (s.ballNum >= BALLS_PER_GAME) {
          s.over = true;
        } else {
          s.ballNum += 1;
          s.respawn = 50;
        }
        setTickets(BALLS_PER_GAME - s.ballNum + (s.over ? 0 : 1));
      }
    }

    // ---- draw ----
    ctx.fillStyle = '#0d1b2e';
    ctx.fillRect(0, 0, W, H);

    // faint playfield art
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.globalAlpha = 0.14;
    ctx.fillStyle = '#c9a84c';
    ctx.font = 'bold 22px Georgia, serif';
    ctx.fillText('CALL 811', W / 2, 300);
    ctx.font = 'bold 12px Georgia, serif';
    ctx.fillText('BEFORE YOU DIG', W / 2, 322);
    ctx.globalAlpha = 1;

    // dashed "buried line" locate marks through each bumper
    ctx.setLineDash([8, 6]);
    ctx.lineWidth = 2;
    s.bumpers.forEach((bp) => {
      ctx.globalAlpha = 0.22;
      ctx.strokeStyle = bp.color;
      ctx.beginPath();
      ctx.moveTo(0, bp.y);
      ctx.lineTo(W, bp.y);
      ctx.stroke();
    });
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;

    // guides
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = GUIDE_R * 2;
    ctx.lineCap = 'round';
    GUIDES.forEach((g) => {
      ctx.beginPath();
      ctx.moveTo(g.ax, g.ay);
      ctx.lineTo(g.bx, g.by);
      ctx.stroke();
    });

    // targets
    TARGETS.forEach((t, i) => {
      ctx.beginPath();
      ctx.arc(t.x, t.y, TARGET_R, 0, Math.PI * 2);
      ctx.fillStyle = s.lit[i] ? '#e07b00' : '#0d1b2e';
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#e07b00';
      ctx.stroke();
      ctx.fillStyle = s.lit[i] ? '#fff' : '#e07b00';
      ctx.font = 'bold 12px monospace';
      ctx.fillText(t.label, t.x, t.y + 1);
    });

    // bumpers
    s.bumpers.forEach((bp, i) => {
      if (s.bumperFlash[i] > 0) {
        s.bumperFlash[i] -= 1;
        ctx.beginPath();
        ctx.arc(bp.x, bp.y, bp.r + 5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.35)';
        ctx.fill();
      }
      ctx.beginPath();
      ctx.arc(bp.x, bp.y, bp.r, 0, Math.PI * 2);
      ctx.fillStyle = bp.color;
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#f5e9d0';
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 10px monospace';
      ctx.fillText(bp.label, bp.x, bp.y + 1);
    });

    // flippers
    ctx.strokeStyle = '#c9a84c';
    ctx.lineWidth = FLIP_R * 2;
    FLIPPERS.forEach((f) => {
      const st = s.flippers[f.key];
      ctx.beginPath();
      ctx.moveTo(f.px, f.py);
      ctx.lineTo(f.px + f.sign * Math.cos(st.a) * FLIP_LEN, f.py + Math.sin(st.a) * FLIP_LEN);
      ctx.stroke();
    });

    // ball
    if (b) {
      ctx.beginPath();
      ctx.arc(b.x, b.y, BALL_R, 0, Math.PI * 2);
      ctx.fillStyle = '#f5f5f5';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(b.x - 2, b.y - 2, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();
    } else if (!s.over) {
      ctx.fillStyle = '#c9a84c';
      ctx.font = 'bold 16px monospace';
      ctx.fillText(`LOCATE TICKET ${s.ballNum} OF ${BALLS_PER_GAME}`, W / 2, H / 2);
    }

    // 811 bonus flash
    if (s.bonusFlash > 0) {
      s.bonusFlash -= 1;
      if (Math.floor(s.bonusFlash / 6) % 2 === 0) {
        ctx.fillStyle = '#e07b00';
        ctx.font = 'bold 44px Georgia, serif';
        ctx.fillText('811!', W / 2, 170);
        ctx.font = 'bold 13px Georgia, serif';
        ctx.fillText('ALL UTILITIES LOCATED +811', W / 2, 200);
      }
    }

    setScore(s.score);

    if (s.over) {
      cancelAnimationFrame(rafRef.current);
      setStatus('over');
      onScore(s.score);
      return;
    }
    rafRef.current = requestAnimationFrame(loop);
  }, [onScore]);

  const start = useCallback(() => {
    stateRef.current = {
      ball: null,
      ballNum: 1,
      respawn: 30,
      score: 0,
      over: false,
      lit: [false, false, false],
      bumpers: BUMPERS.map((bp) => ({ ...bp })),
      bumperFlash: [0, 0, 0],
      bonusFlash: 0,
      keys: { left: false, right: false },
      flippers: { left: { a: REST_ANGLE, da: 0 }, right: { a: REST_ANGLE, da: 0 } },
    };
    setScore(0);
    setTickets(BALLS_PER_GAME);
    setStatus('playing');
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(loop);
  }, [loop]);

  useEffect(() => {
    function onKey(down) {
      return (e) => {
        if (e.code !== 'ArrowLeft' && e.code !== 'ArrowRight') return;
        e.preventDefault();
        const s = stateRef.current;
        if (!s) return;
        s.keys[e.code === 'ArrowLeft' ? 'left' : 'right'] = down;
      };
    }
    const kd = onKey(true);
    const ku = onKey(false);
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    return () => {
      window.removeEventListener('keydown', kd);
      window.removeEventListener('keyup', ku);
    };
  }, []);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center justify-between w-full">
        <span className="text-gold-500 font-black text-lg">☎️ {score}</span>
        <span className="text-gray-400 text-xs">🎫 tickets: {tickets}</span>
        <span className="text-gray-500 text-xs">← → flippers</span>
      </div>
      <div className="relative" style={{ width: W, height: H }}>
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          className="rounded-lg border"
          style={{ borderColor: '#1e3a5f' }}
        />
        {status !== 'playing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-lg px-6 text-center" style={{ backgroundColor: 'rgba(13,27,46,0.88)' }}>
            {status === 'idle' && (
              <>
                <p className="text-gold-500 font-black text-2xl">☎️ 811 PINBALL</p>
                <p className="text-gray-300 text-sm">Ring the buried utilities for points. Light <span className="font-black text-gold-500">8·1·1</span> for the big bonus. Don't hit the dirt.</p>
              </>
            )}
            {status === 'over' && (
              <>
                <p className="text-red-400 font-black text-xl">⛏️ LINE STRIKE! 💥</p>
                <p className="text-gray-300 text-sm">{score} points. Next time, call before you dig.</p>
              </>
            )}
            <button
              onClick={start}
              className="px-5 py-2 rounded font-bold uppercase tracking-widest text-sm"
              style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
            >
              {status === 'idle' ? '▶ File a Locate Ticket' : '↻ Re-dig'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
